import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ContentItem } from '@/types/content';
import { formatViews, getDetailPath } from '@/utils/content';

interface HeroSectionProps {
  slides: ContentItem[];
  latest: ContentItem[];
}

export default function HeroSection({ slides, latest }: HeroSectionProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const go = (next: number) => {
    setIndex((next + slides.length) % slides.length);
  };

  return (
    <section className="mx-auto max-w-[1440px] px-4 pt-6 md:px-6 md:pt-8">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="relative h-[320px] overflow-hidden rounded-xl bg-secondary-900 md:h-[440px] lg:col-span-2">
          {slides.map((slide, i) => (
            <Link
              key={slide.id}
              to={getDetailPath(slide)}
              className={`absolute inset-0 transition-opacity duration-700 ${
                i === index ? 'opacity-100' : 'pointer-events-none opacity-0'
              }`}
            >
              <img
                src={slide.image}
                alt={slide.title}
                title={slide.title}
                className="h-full w-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-secondary-950/90 via-secondary-950/40 to-secondary-950/10"></div>
              <div className="absolute inset-x-0 bottom-0 p-5 md:p-8">
                <span className="inline-flex items-center gap-1 rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50">
                  <i className="ri-fire-line"></i>
                  {slide.subCategory}
                </span>
                <h2 className="mt-3 line-clamp-2 max-w-2xl font-heading text-xl font-bold text-background-50 md:text-3xl">
                  {slide.title}
                </h2>
                <p className="mt-2 line-clamp-2 hidden max-w-2xl text-sm leading-relaxed text-background-200 sm:block">
                  {slide.summary}
                </p>
                <div className="mt-4 flex items-center gap-4 text-xs text-background-200">
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {slide.publishTime.slice(0, 10)}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {formatViews(slide.views)}
                  </span>
                  <span className="hidden items-center gap-1 sm:flex">
                    <i className="ri-arrow-right-line"></i>
                    阅读全文
                  </span>
                </div>
              </div>
            </Link>
          ))}

          <button
            type="button"
            aria-label="上一张"
            onClick={() => go(index - 1)}
            className="absolute left-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-secondary-950/40 text-background-50 transition-colors hover:bg-accent-500 md:left-5 md:h-10 md:w-10"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <button
            type="button"
            aria-label="下一张"
            onClick={() => go(index + 1)}
            className="absolute right-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-secondary-950/40 text-background-50 transition-colors hover:bg-accent-500 md:right-5 md:h-10 md:w-10"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>

          <div className="absolute bottom-5 right-5 z-10 hidden items-center gap-2 md:flex">
            {slides.map((s, i) => (
              <button
                key={s.id}
                type="button"
                aria-label={`第 ${i + 1} 张`}
                onClick={() => setIndex(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === index ? 'w-7 bg-accent-500' : 'w-3 bg-background-50/60 hover:bg-background-50'
                }`}
              />
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="mb-3 flex items-center justify-between border-b border-background-200 pb-3">
            <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-flashlight-line text-accent-500"></i>
              最新资讯
            </h3>
            <Link to="/category/news" className="text-xs text-secondary-500 transition-colors hover:text-primary-600">
              更多
            </Link>
          </div>
          <ul className="space-y-1">
            {latest.map((item, i) => (
              <li key={item.id}>
                <Link
                  to={getDetailPath(item)}
                  className="group flex items-start gap-3 rounded-md px-2 py-2.5 transition-colors hover:bg-primary-50"
                >
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded text-xs font-bold ${
                      i < 3 ? 'bg-accent-500 text-background-50' : 'bg-background-200 text-secondary-600'
                    }`}
                  >
                    {i + 1}
                  </span>
                  <span className="line-clamp-2 text-sm leading-snug text-foreground-800 transition-colors group-hover:text-primary-600">
                    {item.title}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}